import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Arrow, Header, Spark } from "../../components/SiteChrome";
import "./styles.scss";

const colors = [
  { name: "Paper", hex: "#F3F0E8", role: "The canvas", className: "paper" },
  { name: "Ink", hex: "#181A18", role: "The voice", className: "ink" },
  { name: "Signal", hex: "#FF4F00", role: "International Orange", className: "signal" },
  { name: "Sage", hex: "#E4E7DB", role: "Project ground", className: "sage" },
  { name: "Stone", hex: "#DEDDD1", role: "Quiet ground", className: "stone" },
  { name: "Forest", hex: "#273B2E", role: "Food Journal", className: "forest" },
  { name: "Midnight", hex: "#203040", role: "Deep contrast", className: "midnight" }
];

const SectionIntro = ({ number, title, note, id }: { number: string; title: string; note: string; id: string }) => (
  <div className="atelier-section-intro">
    <div className="atelier-section-meta"><span>{number} / THE SYSTEM</span><span>{note}</span></div>
    <h2 id={id}>{title}<span>.</span></h2>
  </div>
);

const AtelierPage = () => {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = "The Atelier — Alexandre Toubiana";
    return () => { document.title = previousTitle; };
  }, []);

  return (
    <div className="v2-site atelier-page" id="top">
      <a className="skip-link" href="#atelier-main">Skip to content</a>
      <Header />
      <main id="atelier-main">
        <div className="atelier-dark">
          <section className="atelier-hero" aria-labelledby="atelier-title">
            <div className="atelier-hero-meta"><span>FIELD NOTES / VOL. 01</span><span>EST. 2026</span></div>
            <h1 id="atelier-title">The<br /><span>Atelier<Spark />.</span></h1>
            <div className="atelier-hero-bottom">
              <p>A small visual language for<br />work with personality.</p>
              <a href="#palette">OPEN THE KIT <span className="atelier-down-arrow" aria-hidden="true" /></a>
            </div>
          </section>
        </div>

          <section className="atelier-section atelier-palette" id="palette" aria-labelledby="palette-title">
            <SectionIntro number="01" title="Color" note="A WARM BASE. A SHARP SIGNAL." id="palette-title" />
            <div className="atelier-colors">
              {colors.map((color, index) => (
                <div className={`atelier-color atelier-color--${color.className}`} key={color.name}>
                  <span className="atelier-color-index">0{index + 1}</span>
                  <div className="atelier-color-bottom"><div><strong>{color.name}</strong><span>{color.role}</span></div><code>{color.hex}</code></div>
                </div>
              ))}
            </div>
          </section>

          <section className="atelier-section atelier-type" id="type" aria-labelledby="type-title">
            <SectionIntro number="02" title="Type" note="LOUD HEADLINES. QUIET DETAILS." id="type-title" />
            <div className="atelier-type-grid">
              <div className="atelier-type-display"><div className="atelier-spec-label"><span>01 / DISPLAY</span><span>ARIAL · 900</span></div><p>Make<br />it <em>matter.</em></p><span className="atelier-type-caption">Oversized, close-set, and unapologetically direct.</span></div>
              <div className="atelier-type-side">
                <div className="atelier-type-body"><div className="atelier-spec-label"><span>02 / BODY</span><span>ARIAL · 400—700</span></div><p>Good design gets out of the way. A little character makes it memorable.</p><span className="atelier-type-caption">Short sentences. Room to breathe.</span></div>
                <div className="atelier-type-mono"><div className="atelier-spec-label"><span>03 / UTILITY</span><span>COURIER NEW · 700</span></div><p>BUILD / EXPLORE / MAKE</p><span className="atelier-type-caption">Small labels give the page its editorial rhythm.</span></div>
              </div>
            </div>
          </section>

          <section className="atelier-section atelier-marks" id="marks" aria-labelledby="marks-title">
            <SectionIntro number="03" title="Marks & actions" note="SIMPLE SHAPES. CLEAR INTENT." id="marks-title" />
            <div className="atelier-marks-grid">
              <div className="atelier-mark-card atelier-mark-card--spark"><div className="atelier-spec-label"><span>THE SPARK</span><span>SVG / 08 POINTS</span></div><Spark /><p>A small interruption in the grid.</p></div>
              <div className="atelier-mark-card atelier-mark-card--actions"><div className="atelier-spec-label"><span>THE ACTIONS</span><span>INK / 1PX STROKE</span></div><div className="atelier-action-row"><Link className="atelier-pill" to="/#projects">VIEW PROJECTS <Arrow diagonal /></Link><Link className="atelier-circle" to="/#experience" aria-label="View experience"><Arrow diagonal /></Link></div><p>Outlined at rest. Direct on interaction.</p></div>
              <div className="atelier-mark-card atelier-mark-card--rule"><div className="atelier-spec-label"><span>THE RULE</span><span>1PX / ALWAYS USEFUL</span></div><div className="atelier-rule-demo"><span>01</span><span>02</span><span>03</span></div><p>Fine lines separate ideas without adding weight.</p></div>
            </div>
          </section>

          <section className="atelier-section atelier-layout" id="layout" aria-labelledby="layout-title">
            <SectionIntro number="04" title="Rhythm" note="SPACE IS PART OF THE STORY." id="layout-title" />
            <div className="atelier-layout-grid"><div className="atelier-grid-demo" aria-hidden="true">{Array.from({ length: 12 }, (_, index) => <span key={index}>{String(index + 1).padStart(2, "0")}</span>)}</div><div className="atelier-layout-copy"><strong>Give the work room.</strong><p>Wide gutters, deliberate gaps, and a clear reading order.</p><div><span>OUTER RAIL</span><b>22—72PX</b></div><div><span>CARD GAP</span><b>18PX</b></div><div><span>DIVIDERS</span><b>1PX</b></div></div></div>
          </section>

          <section className="atelier-motion" id="motion" aria-labelledby="motion-title"><div className="atelier-motion-head"><span>05 / MOTION</span><span>CONTINUOUS, NEVER REQUIRED</span></div><h2 id="motion-title">A little<br /><em>movement.</em></h2><div className="atelier-ribbon" aria-label="Motion specimen"><div className="atelier-ribbon-track"><span>BUILD WITH PURPOSE <Spark /> MAKE IT HUMAN <Spark /> STAY CURIOUS <Spark /></span><span aria-hidden="true">BUILD WITH PURPOSE <Spark /> MAKE IT HUMAN <Spark /> STAY CURIOUS <Spark /></span></div></div><p>Motion adds character. Reduced-motion settings keep the message still.</p></section>

        </main>
        <footer className="atelier-footer"><div><span>THE ATELIER / VOL. 01</span><p>Now put it to work<span>.</span></p></div><Link to="/">BACK TO THE PORTFOLIO <Arrow diagonal /></Link></footer>
    </div>
  );
};

export default AtelierPage;
